import { t, translateMessage } from "../i18n";
import {
  ArrowLeft,
  ArrowRightLeft,
  Check,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  ClipboardList,
  Minus,
  Pencil,
  Plus,
  ShoppingBasket,
  Search,
  Trash2,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CorrectionDialog } from "../components/CorrectionDialog";
import { PrinterSplitButton } from "../components/PrinterSplitButton";
import { ProductDetailsDialog } from "../components/ProductDetailsDialog";
import { VariationsDialog } from "../components/VariationsDialog";
import { useBackHandler } from "../backNavigation";
import type {
  DraftLine,
  OrderLine,
  Orders,
  PendingSubmission,
  PrinterConfig,
  Product,
  Setup
} from "../types";
import {
  loadCloseTableButtonStyle,
  loadDetailsPromptOnAdd,
  loadProductDisplayMode,
  loadProductImagesVisible,
  loadProductScrollMode,
  loadSticky
} from "../storage";
import {
  draftKey,
  draftName,
  draftPrintName,
  draftTotal,
  draftUnitPrice,
  effectiveOrderLines,
  formatMoney,
  isOrderRecorded,
  latestOpenOrder,
  normalize,
  splitVariations
} from "../utils";

type Props = {
  tableLabel: string;
  setup: Setup;
  orders: Orders | null;
  draft: DraftLine[];
  pending?: PendingSubmission;
  printers: PrinterConfig[];
  busy: boolean;
  canEdit: boolean;
  canCancel: boolean;
  onBack: () => void;
  onDraftChange: (lines: DraftLine[]) => void;
  onSubmit: (printerIds: number[]) => void;
  onCloseTable: () => void;
  onSetServed: (lineIds: number[], served: boolean) => void;
  onCancelOrder: (orderId: number) => void;
  /* Moving an open order happens on the tables grid, as it does on Android, so the screen only
     has to hand the order over. */
  onBeginChangeTable: (orderId: number) => void;
  onCorrectProduct: (input: {
    orderId: number;
    lineId: number;
    targetQuantity: number;
    withoutOptions: string;
    extras: string;
    counters: string;
    itemComment: string;
    takeaway: boolean;
    price: number;
  }) => void;
};

type DetailFlow =
  | {
      mode: "single";
      line: DraftLine;
      originalKey: string | null;
    }
  | {
      mode: "variations";
      line: DraftLine;
      lines: DraftLine[];
      productId: number;
    };

function createLine(product: Product, variation = "", quantity = 1): DraftLine {
  return {
    key: draftKey(product.id, variation),
    productId: product.id,
    name: draftName(product, variation),
    printName: draftPrintName(product, variation),
    variation,
    quantity,
    withoutOptions: [],
    extras: [],
    counters: {},
    comment: "",
    takeaway: false,
    unitPrice: draftUnitPrice(product, variation, {})
  };
}

function pendingLabel(item?: PendingSubmission): string {
  if (!item) {
    return t("Αποστολή");
  }
  if (item.state === "failed") {
    return t("Επανάληψη εκτύπωσης");
  }
  if (item.state === "unknown") {
    return t("Έλεγχος εκτύπωσης");
  }
  if (item.state === "order_pending") {
    return t("Επανάληψη αποστολής");
  }
  if (item.state === "print_pending") {
    return t("Επανάληψη εκτύπωσης");
  }
  return t("Εκτύπωση...");
}

export function OrderScreen({
  tableLabel,
  setup,
  orders,
  draft,
  pending,
  printers,
  busy,
  canEdit,
  canCancel,
  onBack,
  onDraftChange,
  onSubmit,
  onCloseTable,
  onSetServed,
  onCancelOrder,
  onBeginChangeTable,
  onCorrectProduct
}: Props) {
  const [query, setQuery] = useState("");
  const categories = useMemo(
    () => setup.categories.slice().sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [setup.categories]
  );
  const [categoryId, setCategoryId] = useState<number | null>(() => categories[0]?.id ?? null);
  const [detailFlow, setDetailFlow] = useState<DetailFlow | null>(null);
  const [variationProductId, setVariationProductId] = useState<number | null>(null);
  const [existingOpen, setExistingOpen] = useState(true);
  const [correctionLine, setCorrectionLine] = useState<OrderLine | null>(null);
  const [confirmCancelOrder, setConfirmCancelOrder] = useState(false);
  const [mobilePane, setMobilePane] = useState<"catalog" | "draft">("catalog");
  useBackHandler(mobilePane === "draft", () => setMobilePane("catalog"), "page");
  useBackHandler(confirmCancelOrder, () => setConfirmCancelOrder(false));
  const productDisplayMode = loadProductDisplayMode();
  const productImagesVisible = productDisplayMode === "tiles" && loadProductImagesVisible();
  const detailsPromptOnAdd = loadDetailsPromptOnAdd();
  const closeButtonStyle = loadCloseTableButtonStyle();

  useEffect(() => {
    if (!categories.some((category) => category.id === categoryId)) {
      setCategoryId(categories[0]?.id ?? null);
    }
  }, [categories, categoryId]);

  const products = useMemo(() => {
    const categoryOrder = new Map(categories.map((category, index) => [category.id, index]));
    const needle = normalize(query);
    return setup.products
      .filter((product) => product.active)
      // Android searches the complete menu, while an empty query shows only the selected
      // category. Keeping the same rule also removes the need for an artificial "All" tab.
      .filter((product) => Boolean(needle) || product.category_id === categoryId)
      .filter((product) => {
        if (!needle) {
          return true;
        }
        return normalize(`${product.name} ${product.aliases}`).includes(needle);
      })
      .sort((a, b) =>
        (categoryOrder.get(a.category_id) ?? Number.MAX_SAFE_INTEGER) -
          (categoryOrder.get(b.category_id) ?? Number.MAX_SAFE_INTEGER) ||
        a.sort_order - b.sort_order ||
        a.id - b.id
      );
  }, [categories, categoryId, query, setup.products]);

  const openOrder = latestOpenOrder(orders, tableLabel);
  const existingLines = openOrder ? effectiveOrderLines(orders, openOrder.id) : [];
  const existingTotal = existingLines.reduce(
    (total, line) => total + line.price * line.quantity,
    0
  );
  const detailsLine = detailFlow?.line;
  const detailsProduct = detailsLine
    ? setup.products.find((product) => product.id === detailsLine.productId)
    : undefined;
  const variationProduct = variationProductId
    ? setup.products.find((product) => product.id === variationProductId)
    : undefined;
  // Once the server holds the order the ticket stays reprintable from the history, so no state
  // of the printer should strand the table. Only an order that has not reached the server yet
  // holds the close back, because closing then could lose it.
  const closeAllowed = !pending || isOrderRecorded(pending, orders);
  const screenClasses = [
    "screen",
    "order-screen",
    `scroll-${loadProductScrollMode()}`,
    loadSticky("categories") ? "sticky-categories" : "",
    loadSticky("actions") ? "sticky-actions" : "",
    loadSticky("tabs") ? "sticky-order-tabs" : "",
    `mobile-pane-${mobilePane}`
  ].filter(Boolean).join(" ");

  function updateQuantity(key: string, quantity: number) {
    if (quantity <= 0) {
      onDraftChange(draft.filter((line) => line.key !== key));
      return;
    }
    onDraftChange(draft.map((line) =>
      line.key === key ? { ...line, quantity: Math.min(99, quantity) } : line
    ));
  }

  function addProduct(product: Product) {
    const variations = splitVariations(product.variation_options);
    if (variations.length) {
      setVariationProductId(product.id);
      return;
    }
    const key = draftKey(product.id, "");
    const existing = draft.find((line) => line.key === key);
    const line = existing
      ? { ...existing, quantity: Math.min(99, existing.quantity + 1) }
      : createLine(product);
    if (!detailsPromptOnAdd) {
      onDraftChange(existing
        ? draft.map((candidate) => candidate.key === existing.key ? line : candidate)
        : [...draft, line]);
      return;
    }
    setDetailFlow({ mode: "single", line, originalKey: existing?.key ?? null });
  }

  function saveVariations(quantities: Record<string, number>) {
    if (!variationProduct) {
      return;
    }
    const untouched = draft.filter((line) => line.productId !== variationProduct.id);
    const changed = splitVariations(variationProduct.variation_options)
      .filter((variation) => (quantities[variation] ?? 0) > 0)
      .map((variation) => {
        const existing = draft.find(
          (line) => line.productId === variationProduct.id && line.variation === variation
        );
        return existing
          ? { ...existing, quantity: quantities[variation] }
          : createLine(variationProduct, variation, quantities[variation]);
      });
    setVariationProductId(null);
    if (!changed.length) {
      onDraftChange(untouched);
      return;
    }
    if (!detailsPromptOnAdd) {
      onDraftChange([...untouched, ...changed]);
      return;
    }
    setDetailFlow({
      mode: "variations",
      line: { ...changed[0] },
      lines: changed,
      productId: variationProduct.id
    });
  }

  function saveDetails(line: DraftLine | null) {
    if (!detailFlow) {
      return;
    }
    if (detailFlow.mode === "single") {
      const next = line
        ? detailFlow.originalKey
          ? draft.map((candidate) => candidate.key === detailFlow.originalKey ? line : candidate)
          : [...draft, line]
        : detailFlow.originalKey
          ? draft.filter((candidate) => candidate.key !== detailFlow.originalKey)
          : draft;
      onDraftChange(next);
      setDetailFlow(null);
      return;
    }
    if (!line || !detailsProduct) {
      setDetailFlow(null);
      return;
    }
    const nextLines = detailFlow.lines.map((candidate) => ({
      ...candidate,
      withoutOptions: [...line.withoutOptions],
      extras: [...line.extras],
      counters: { ...line.counters },
      comment: line.comment,
      takeaway: line.takeaway,
      unitPrice: draftUnitPrice(detailsProduct, candidate.variation, line.counters, line.extras)
    }));
    onDraftChange([
      ...draft.filter((candidate) => candidate.productId !== detailFlow.productId),
      ...nextLines
    ]);
    setDetailFlow(null);
  }

  return (
    <section className={screenClasses} aria-labelledby="order-heading">
      <div className="order-heading">
        <button type="button" className="icon-button back-command" onClick={onBack} aria-label={t("Πίσω")}>
          <ArrowLeft />
        </button>
        <div>
          <h1 id="order-heading">{t("Τραπέζι ")}{tableLabel}</h1>
          <p>{openOrder ? t("Ανοιχτή παραγγελία #{0}", openOrder.order_number) : t("Νέα παραγγελία")}</p>
        </div>
        {canEdit && (openOrder || draft.length > 0) ? (
          <button
            type="button"
            className="secondary-command order-heading-action"
            /* Zero means there is no order yet, so the unsent draft is what moves. */
            onClick={() => onBeginChangeTable(openOrder ? openOrder.id : 0)}
          >
            <ArrowRightLeft />
            <span>{t("Αλλαγή τραπεζιού")}</span>
          </button>
        ) : null}
      </div>

      <div className="order-mobile-tabs" role="tablist" aria-label={t("Περιεχόμενο παραγγελίας")}>
        <button type="button" className={mobilePane === "catalog" ? "active" : ""} onClick={() => setMobilePane("catalog")} role="tab" aria-selected={mobilePane === "catalog"}>
          <ShoppingBasket />
          <span>{t("Προϊόντα")}</span>
        </button>
        <button type="button" className={mobilePane === "draft" ? "active" : ""} onClick={() => setMobilePane("draft")} role="tab" aria-selected={mobilePane === "draft"}>
          <ClipboardList />
          <span>{t("Παραγγελία")}</span>
          {draft.length ? <b>{draft.reduce((sum, line) => sum + line.quantity, 0)}</b> : null}
        </button>
      </div>

      <div className="order-workspace">
        <section className="catalog-pane">
          <label className="search-field">
            <Search />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("Αναζήτηση προϊόντος")}
              aria-label={t("Αναζήτηση προϊόντος")}
            />
            {query ? (
              <button type="button" className="icon-button" onClick={() => setQuery("")} aria-label={t("Καθαρισμός")}>
                <X />
              </button>
            ) : null}
          </label>

          <div className="category-strip" role="tablist" aria-label={t("Κατηγορίες")}>
            {categories.map((category) => (
              <button
                type="button"
                className={!query.trim() && categoryId === category.id ? "active" : ""}
                onClick={() => {
                  setCategoryId(category.id);
                  setQuery("");
                }}
                role="tab"
                aria-selected={!query.trim() && categoryId === category.id}
                key={category.id}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className={`product-grid product-view-${productDisplayMode}`}>
            {/* Inside the grid so it scrolls away with the products; only the categories stay. */}
            <h2 className="catalog-section-heading">{t("Προϊόντα")}</h2>
            {products.map((product) => {
              const selectedQuantity = draft
                .filter((line) => line.productId === product.id)
                .reduce((sum, line) => sum + line.quantity, 0);
              return (
                <button
                  type="button"
                  className={selectedQuantity ? "product-tile selected" : "product-tile"}
                  onClick={() => addProduct(product)}
                  key={product.id}
                >
                  {productDisplayMode === "tiles" && productImagesVisible && product.image_data ? (
                    <img
                      className="product-image"
                      src={product.image_data}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                  <span className="product-name">{product.name}</span>
                  <span className="product-price">{formatMoney(product.price)}</span>
                  {selectedQuantity ? <strong className="product-count">{selectedQuantity}x</strong> : null}
                </button>
              );
            })}
          </div>
          {!products.length ? <div className="empty-state">{t("Δεν βρέθηκαν προϊόντα.")}</div> : null}
        </section>

        <aside className="draft-pane" aria-label={t("Νέα προϊόντα")}>
          <div className="draft-heading">
            <div>
              <h2>{t("Νέα προϊόντα")}</h2>
              <p>{draft.reduce((sum, line) => sum + line.quantity, 0)}{t(" τεμάχια")}</p>
            </div>
            {draft.length ? (
              <button type="button" className="icon-button danger-icon" onClick={() => onDraftChange([])} aria-label={t("Καθαρισμός")}>
                <Trash2 />
              </button>
            ) : null}
          </div>
          <div className="draft-lines">
            {draft.map((line) => {
              const product = setup.products.find((candidate) => candidate.id === line.productId);
              return (
                <article className="draft-line" key={line.key}>
                  <div className="draft-line-copy">
                    <strong>{line.name}</strong>
                    <span>{formatMoney(line.unitPrice * line.quantity)}</span>
                    {line.withoutOptions.length ? <small>{t("Χωρίς: ")}{line.withoutOptions.join(", ")}</small> : null}
                    {line.extras.length ? <small>Extra: {line.extras.join(", ")}</small> : null}
                    {line.comment ? <small>{t("Σχόλιο: ")}{line.comment}</small> : null}
                  </div>
                  <div className="draft-line-actions">
                    <span className="compact-stepper">
                      <button type="button" onClick={() => updateQuantity(line.key, line.quantity - 1)} aria-label={t("Μείωση")}>
                        <Minus />
                      </button>
                      <strong>{line.quantity}</strong>
                      <button type="button" onClick={() => updateQuantity(line.key, line.quantity + 1)} aria-label={t("Αύξηση")}>
                        <Plus />
                      </button>
                    </span>
                    {product ? (
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => setDetailFlow({
                          mode: "single",
                          line: { ...line },
                          originalKey: line.key
                        })}
                        aria-label={t("Λεπτομέρειες {0}", line.name)}
                      >
                        <Pencil />
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
            {!draft.length ? <div className="empty-state compact">{t("Δεν έχουν προστεθεί προϊόντα.")}</div> : null}
          </div>
          <div className="draft-total">
            <span>{t("Σύνολο")}</span>
            <strong>{formatMoney(draftTotal(draft))}</strong>
          </div>
          {existingLines.length ? (
            <section className="existing-order-band">
              <button
                type="button"
                className="band-heading"
                onClick={() => setExistingOpen((value) => !value)}
                aria-expanded={existingOpen}
              >
                <span>
                  <CircleCheck />
                  <strong>{t("Ανοιχτή παραγγελία")}</strong>
                  <small>{existingLines.reduce((sum, line) => sum + line.quantity, 0)}{t(" είδη")}</small>
                </span>
                {existingOpen ? <ChevronUp /> : <ChevronDown />}
              </button>
              {existingOpen ? (
                <div className="existing-lines">
                  {existingLines.map((line) => (
                    <div className={line.served ? "existing-line served" : "existing-line"} key={line.id}>
                      <label className="existing-line-main">
                        <input
                          type="checkbox"
                          checked={Boolean(line.served)}
                          onChange={(event) => onSetServed(line.source_line_ids, event.target.checked)}
                        />
                        <strong>{line.quantity}x</strong>
                        <span>{line.name}</span>
                      </label>
                      {canEdit ? (
                        <button
                          type="button"
                          className="icon-button"
                          onClick={() => setCorrectionLine(line)}
                          aria-label={t("Διόρθωση {0}", line.name)}
                          disabled={busy}
                        >
                          <Pencil />
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {existingTotal > 0 ? (
                    <div className="existing-order-total">
                      <span>{t("Σύνολο")}</span>
                      <strong>{formatMoney(existingTotal)}</strong>
                    </div>
                  ) : null}
                  {openOrder && canCancel ? (
                    <button
                      type="button"
                      className="secondary-command danger-command"
                      onClick={() => setConfirmCancelOrder(true)}
                      disabled={busy}
                    >
                      <X />
                      <span>{t("Ακύρωση παραγγελίας")}</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}
        </aside>
      </div>

      {pending?.error ? <div className={`inline-alert ${pending.state}`}>{translateMessage(pending.error)}</div> : null}

      <footer className={`order-sticky-actions close-button-${closeButtonStyle}`}>
        {openOrder ? (
          <button
            type="button"
            className="secondary-command close-table-command"
            onClick={onCloseTable}
            disabled={busy || !closeAllowed}
            aria-label={t("Κλείσιμο τραπεζιού")}
            title={t("Κλείσιμο τραπεζιού")}
          >
            <Check />
            <span>{t("Κλείσιμο τραπεζιού")}</span>
          </button>
        ) : null}
        <PrinterSplitButton
          label={pendingLabel(pending)}
          printers={printers}
          busy={busy || (!draft.length && !pending) || pending?.state === "printing"}
          menuDisabled={Boolean(pending)}
          automaticMain
          onPrint={onSubmit}
        />
      </footer>

      {detailsLine && detailsProduct ? (
        <ProductDetailsDialog
          line={detailsLine}
          product={detailsProduct}
          showQuantity={detailFlow?.mode !== "variations"}
          onCancel={() => setDetailFlow(null)}
          onSave={saveDetails}
        />
      ) : null}
      {variationProduct ? (
        <VariationsDialog
          product={variationProduct}
          draftLines={draft}
          onCancel={() => setVariationProductId(null)}
          onSave={saveVariations}
        />
      ) : null}
      {correctionLine && openOrder && canEdit ? (
        <CorrectionDialog
          line={correctionLine}
          product={setup.products.find((product) => product.id === correctionLine.product_id)}
          busy={busy}
          onCancel={() => setCorrectionLine(null)}
          onSave={(result) => {
            onCorrectProduct({
              orderId: openOrder.id,
              lineId: correctionLine.id,
              ...result
            });
            setCorrectionLine(null);
          }}
        />
      ) : null}
      {confirmCancelOrder && openOrder && canCancel ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Ακύρωση παραγγελίας")}>
            <header className="dialog-header">
              <div>
                <h2>{t("Ακύρωση παραγγελίας")}</h2>
                <p>{t("Τραπέζι ")}{tableLabel}{t(" • Κωδικός #")}{openOrder.order_number}</p>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setConfirmCancelOrder(false)}
                aria-label={t("Κλείσιμο")}
              >
                <X />
              </button>
            </header>
            <div className="dialog-scroll">
              <p>{t("Θα ακυρωθεί ολόκληρη η παραγγελία και θα τυπωθεί δελτίο ακύρωσης.")}</p>
            </div>
            <footer className="dialog-actions">
              <button
                type="button"
                className="secondary-command"
                onClick={() => setConfirmCancelOrder(false)}
                disabled={busy}
              >
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={busy}
                onClick={() => {
                  onCancelOrder(openOrder.id);
                  setConfirmCancelOrder(false);
                }}
              >
                <X />
                <span>{t("Ακύρωση παραγγελίας")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
}
